import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Phone, Mail, MapPin, MessageCircle, Clock, Send, CheckCircle2, Loader2, ShieldCheck,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SocialIcons from '../components/SocialIcons';
import { trackFormSubmit, trackCallClick, trackWhatsAppClick, gtmLeadSubmit } from '../lib/tracker';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_INFO = [
  {
    Icon: Phone,
    title: 'Call Us',
    primary: '+91 78199 88010',
    secondary: 'Mon–Sat · 8 AM – 9 PM',
    href: 'tel:+917819988010',
    tint: 'from-emerald-50 to-emerald-100 text-emerald-700',
  },
  {
    Icon: MessageCircle,
    title: 'WhatsApp',
    primary: '+91 94101 10910',
    secondary: 'Chat 24/7 · Instant replies',
    href: 'https://api.whatsapp.com/send?phone=919410110910',
    external: true,
    tint: 'from-green-50 to-green-100 text-green-700',
  },
  {
    Icon: Mail,
    title: 'Email',
    primary: 'info.entorid@gmail.com',
    secondary: 'Replies within 4 hours',
    href: 'mailto:info.entorid@gmail.com',
    tint: 'from-blue-50 to-blue-100 text-blue-700',
  },
  {
    Icon: MapPin,
    title: 'Service Area',
    primary: 'Dehradun · Haridwar · Rishikesh',
    secondary: 'Serving entire Uttarakhand',
    tint: 'from-amber-50 to-amber-100 text-amber-700',
  },
];

export default function Contact({ onOpenCalculator }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | error

  useSeo(
    'Contact EntoRid — Pest Control Service Booking & Free Inspection',
    'Get in touch with EntoRid pest control. Call +91 78199 88010, WhatsApp +91 94101 10910 (24/7), or fill our quick form. Free on-site inspection across Uttarakhand.'
  );

  const isValid = name.trim() && phone.length >= 10 && city.trim() && message.trim().length >= 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || state === 'sending') return;
    setState('sending');
    try {
      const res = await axios.post(`${API_BASE}/leads`, {
        name: name.trim(),
        phone,
        email: email.trim() || null,
        city: city.trim(),
        pest_type: 'other',
        pest_label: 'Contact Form Inquiry',
        pest_description: message.trim(),
        additional_details: message.trim(),
        estimated_price: null,
        source: 'contact-page',
      }, { timeout: 8000 });
      setState('success');
      trackFormSubmit({ source: 'contact_page' });
      gtmLeadSubmit({
        leadId: res?.data?.id || null,
        segment: 'residential',
        pestType: 'other',
        pestLabel: 'Contact Form Inquiry',
        value: 0,
        city: city.trim(),
        source: 'contact-page',
      });
    } catch (err) {
      console.error('Contact submit failed', err);
      setState('error');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Contact</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-emerald-900 text-white">
        <div aria-hidden className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="contact-title">
            Get in Touch
          </h1>
          <p className="mt-3 text-sm sm:text-lg text-white/85 max-w-2xl leading-relaxed">
            Our experts respond within minutes. Call, WhatsApp, or fill the form below — we'll handle the rest.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Contact cards */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-12 sm:mb-16">
          {CONTACT_INFO.map(({ Icon, title, primary, secondary, href, external, tint }) => {
            const inner = (
              <>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tint} flex items-center justify-center ring-1 ring-slate-100`}>
                  <Icon size={22} />
                </div>
                <h3 className="mt-3 text-sm font-bold uppercase tracking-wider text-gray-500">{title}</h3>
                <div className="mt-1 text-base sm:text-lg font-bold text-slate-900 break-words">{primary}</div>
                <div className="mt-0.5 text-xs sm:text-sm text-gray-500">{secondary}</div>
              </>
            );
            return href ? (
              <a
                key={title}
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noreferrer' : undefined}
                data-testid={`contact-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="block bg-white rounded-2xl p-5 ring-1 ring-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {inner}
              </a>
            ) : (
              <div key={title} className="bg-white rounded-2xl p-5 ring-1 ring-gray-100 shadow-sm">
                {inner}
              </div>
            );
          })}
        </section>

        {/* Form + side info */}
        <section className="grid lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm p-6 sm:p-8">
              {state === 'success' ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                    <CheckCircle2 size={32} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Thanks {name.split(' ')[0]}!</h2>
                  <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
                    Our pest-control expert will contact you within <strong>2 hours</strong> on <strong>{phone}</strong>.
                    For urgent help, WhatsApp us now.
                  </p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="https://api.whatsapp.com/send?phone=919410110910"
                      target="_blank" rel="noreferrer"
                      className="h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={16} /> Open WhatsApp
                    </a>
                    <a
                      href="tel:+917819988010"
                      className="h-12 px-6 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold inline-flex items-center justify-center gap-2"
                    >
                      <Phone size={16} /> Call Now
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Send us a message</h2>
                  <p className="mt-1.5 text-sm text-gray-500">We typically reply within 1-2 hours during working hours.</p>
                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-800">Full Name *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className="mt-1.5 h-11" data-testid="contact-name" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-800">Phone *</Label>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="10-digit number"
                          className="mt-1.5 h-11"
                          maxLength={10}
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-800">Email (optional)</Label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="mt-1.5 h-11"
                          data-testid="contact-email"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-800">City *</Label>
                        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g., Dehradun" className="mt-1.5 h-11" data-testid="contact-city" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-800">How can we help? *</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us about the pest issue — type of pest, area affected, urgency, etc."
                        rows={4}
                        className="mt-1.5 resize-none"
                        data-testid="contact-message"
                      />
                    </div>

                    {state === 'error' && (
                      <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                        Something went wrong. Please try WhatsApp at +91 94101 10910.
                      </p>
                    )}

                    <Button
                      type="submit"
                      disabled={!isValid || state === 'sending'}
                      data-testid="contact-submit"
                      className={`w-full h-12 rounded-xl text-base font-semibold transition-all ${
                        isValid && state !== 'sending'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {state === 'sending'
                        ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>)
                        : (<><Send size={16} className="mr-2" /> Send Message</>)}
                    </Button>

                    <p className="text-[11px] text-center text-gray-500 flex items-center justify-center gap-1 pt-1">
                      <ShieldCheck size={11} className="text-emerald-600" /> Your information is safe with us. We never share or spam.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* Side panel */}
          <aside className="lg:col-span-2 space-y-5">
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-5 sm:p-6 ring-1 ring-emerald-100/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
                  <Clock size={18} />
                </div>
                <h3 className="text-base font-bold text-slate-900">Working Hours</h3>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                <li className="flex justify-between"><span>Monday – Saturday</span><span className="font-semibold">8 AM – 9 PM</span></li>
                <li className="flex justify-between"><span>Sunday</span><span className="font-semibold">10 AM – 6 PM</span></li>
                <li className="flex justify-between"><span>Emergency</span><span className="font-semibold text-emerald-700">24/7 via WhatsApp</span></li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-5 sm:p-6 ring-1 ring-gray-100 shadow-sm">
              <h3 className="text-base font-bold text-slate-900">Prefer instant booking?</h3>
              <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">Use our real-time price calculator to get an instant quote and book directly.</p>
              <Button
                onClick={onOpenCalculator}
                data-testid="contact-side-book"
                className="mt-4 w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                Open Price Calculator
              </Button>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-5 sm:p-6">
              <h3 className="text-base font-bold">For commercial / bulk inquiries</h3>
              <p className="mt-1.5 text-sm text-white/75 leading-relaxed">
                Restaurants, warehouses, factories or societies — get a tailored AMC plan.
              </p>
              <a
                href="mailto:info.entorid@gmail.com?subject=Commercial%20Pest%20Control%20Inquiry"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
              >
                info.entorid@gmail.com →
              </a>
            </div>
          </aside>
        </section>

        {/* Follow Us */}
        <section className="bg-white border-y border-slate-200 py-12">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Follow EntoRid&trade; on Social Media</h2>
            <p className="text-sm text-slate-600 mb-6">Stay connected for pest-control tips, exclusive offers, and service updates</p>
            <div className="flex justify-center">
              <SocialIcons variant="outline" size="lg" />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
